import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LinkStyle} from '@ui/buttons/external-link';
import {RegisterPayload, useRegister} from '../requests/use-register';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '@ui/settings/use-settings';
import React, {useContext} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';

interface Props {
  inviteType?: string;
}
export function RegisterPage({inviteType}: Props) {
  const {branding, registration, social} = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();

  const isRegisteringUsingInvite =
    pathname.includes('workspace') || !!inviteType;
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: {email: searchParamsEmail, creator_roles: [], terms_accepted: false, profile_public: false, open_to_collaboration: false, marketing_opt_in: false},
  });
  const register = useRegister(form);

  if (registration?.disable && !isRegisteringUsingInvite) {
    return <Navigate to="/login" replace />;
  }

  let heading = <Trans message="Create a new account" />;
  if (isRegisteringUsingInvite) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  }

  const message = (
    <Trans
      values={{
        a: parts => (
          <Link className={LinkStyle} to="/login">
            {parts}
          </Link>
        ),
      }}
      message="Already have an account? <a>Sign in.</a>"
    />
  );

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={async payload => {
          const isValid = await verify();
          if (isValid) {
            register.mutate({
              ...payload,
              invite_type: inviteType,
            });
          }
        }}
      >
        <FormTextField className="mb-24" name="display_name" label="Display / artist name" required minLength={2} maxLength={100} autoComplete="nickname" />
        <FormTextField className="mb-24" name="username" label="Unique username" required minLength={3} maxLength={30} pattern="[a-z0-9_]+" description="3–30 lowercase letters, numbers or underscores." autoComplete="username" />
        <FormTextField
          className="mb-32"
          name="email"
          type="email"
          disabled={!!searchParamsEmail}
          label={<Trans message="Email" />}
          required
        />
        <FormTextField
          className="mb-32"
          name="password"
          type="password"
          label={<Trans message="Password" />}
          required
        />
        <FormTextField
          className="mb-32"
          name="password_confirmation"
          type="password"
          label={<Trans message="Confirm password" />}
          required
        />
        {auth?.registerFields ? <auth.registerFields /> : null}
        <details className="mb-24 rounded border p-16">
          <summary className="cursor-pointer font-semibold">Creator details — optional</summary>
          <p className="my-16 text-sm">Tell us what you create. You can add your photo and social links in Edit profile after signing up.</p>
          <fieldset className="mb-16"><legend className="mb-8">Creative roles</legend>
            {['Artist','Producer','Vocalist','DJ','Songwriter','Engineer','Content creator'].map(role => (
              <label key={role} className="mb-8 flex items-center gap-8"><input type="checkbox" value={role} {...form.register('creator_roles')} />{role}</label>
            ))}
          </fieldset>
          <FormTextField className="mb-16" name="genres" label="Genres and interests" maxLength={250} />
          <FormTextField className="mb-16" name="city" label="City or region" maxLength={100} />
          <FormTextField className="mb-16" name="credit_name" label="Preferred credit name" maxLength={100} />
          <FormTextField className="mb-16" name="bio" label="Short bio" inputElementType="textarea" rows={3} maxLength={1000} />
          <FormCheckbox className="mb-12 block" name="open_to_collaboration">I am open to collaboration</FormCheckbox>
          <FormCheckbox className="mb-12 block" name="profile_public">Make my creator profile details public</FormCheckbox>
          <p className="text-sm">Your display name may appear with your activity. Email stays private. Songs are shared separately, only with people you invite.</p>
        </details>
        <FormCheckbox className="mb-16 block" name="terms_accepted" required>
          I agree to the <a className={LinkStyle} href="/signup-policies/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a> and <a className={LinkStyle} href="/signup-policies/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </FormCheckbox>
        <FormCheckbox className="mb-24 block" name="marketing_opt_in">Email me IEST news, events and promotions (optional)</FormCheckbox>
        <PolicyCheckboxes />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={register.isPending || isVerifying}
        >
          <Trans message="Create account" />
        </Button>
        <SocialAuthSection
          dividerMessage={
            social.compact_buttons ? (
              <Trans message="Or sign up with" />
            ) : (
              <Trans message="OR" />
            )
          }
        />
      </Form>
    </AuthLayout>
  );
}

function PolicyCheckboxes() {
  const {registration} = useSettings();

  if (!registration?.policies) return null;

  return (
    <div className="mb-32">
      {registration.policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="mb-4 block"
          required
        >
          <Trans
            message="I accept the :name"
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}
